/**
 * Application configuration.
 * In production, these values come from environment variables
 * injected at build time via REACT_APP_ prefix.
 */

/**
 * Resolve the backend API base URL.
 *  1. Explicit REACT_APP_API_BASE_URL always wins (set it if the API lives
 *     on a different host).
 *  2. Local CRA dev server (localhost:3000) → talk to the local backend on
 *     port 3001.
 *  3. Otherwise → same-origin `/api`. This is the SINGLE-APP-SERVICE case:
 *     the Express backend serves this build AND the API from one host, so a
 *     relative path just works regardless of the deployed hostname.
 */
function resolveApiBaseUrl() {
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    const isLocalDev =
      (hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000';
    if (isLocalDev) return 'http://localhost:3001/api';
  }
  return '/api';
}

const config = {
  // Backend API
  apiBaseUrl: resolveApiBaseUrl(),

  // Azure AD / MSAL
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
    // Where AAD returns the user after sign-out. Defaults to the app root
    // so the user lands on the (auto-login) landing page again.
    postLogoutRedirectUri:
      process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || window.location.origin,
    scopes: [process.env.REACT_APP_API_SCOPE],
    // When true (default), hitting any URL while unauthenticated immediately
    // redirects to Azure AD sign-in. Set REACT_APP_AUTO_LOGIN=false to show a
    // manual "Sign in" landing page instead.
    autoLogin: (process.env.REACT_APP_AUTO_LOGIN || 'true').toLowerCase() !== 'false',
  },

  // Role-based access control.
  // `adminRole` gates admin-only UI (the Settings tab). Configurable via
  // REACT_APP_ADMIN_ROLE so it can match whatever the Entra app-role is
  // named. Role comparisons in the UI are case-insensitive.
  rbac: {
    adminRole: process.env.REACT_APP_ADMIN_ROLE || 'SystemAdmin',
    // Role that gates the Model Testing tab. Separate from adminRole so the
    // test portal can be granted to a wider group. Case-insensitive.
    modelTestRole: process.env.REACT_APP_MODEL_TEST_ROLE || 'EASP_Admin',
    // Role that gates the APIM Onboarding APPROVAL queue. Any user can submit
    // an onboarding request; only these roles see/act on the approval queue.
    // Accepts a COMMA-SEPARATED list so the queue can be opened to more than
    // one role (e.g. "EASP_Admin,SystemAdmin"). Always an array.
    easpApproverRole: (process.env.REACT_APP_EASP_APPROVER_ROLE || 'EASP_Admin')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean),
  },

  // Polling
  pollingInterval: parseInt(process.env.REACT_APP_POLLING_INTERVAL || '5000', 10), // 5 seconds
  dashboardRefreshInterval: parseInt(process.env.REACT_APP_DASHBOARD_REFRESH || '30000', 10), // 30 seconds

  // UI defaults
  defaultTimeRange: '24h',
  defaultGranularity: '1h',
  realtimeLimit: 100,

  // ApiManagementGatewayLogs are retained for a limited window (default 45
  // days). Pages whose data is sourced from those logs must not offer a time
  // range beyond this, or the UI would silently query empty buckets. Keep in
  // sync with the backend GATEWAY_LOG_RETENTION_DAYS setting.
  gatewayRetentionDays: parseInt(
    process.env.REACT_APP_GATEWAY_LOG_RETENTION_DAYS || '45',
    10,
  ),
};

export default config;
